import { Alert, AlertDescription, AlertTitle, Badge, Button, Input } from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';

import type { IVocabulary } from '../api/definitions-contracts';
import {
  ChoiceField,
  FieldRow,
  NumberField,
  PayloadHeader,
  RawJsonField,
  RepeaterItem,
  StringListField,
  TextField,
  TriStateField,
  UnknownKeysNotice,
  asArray,
  asObject,
  asString,
  emptyToNull,
  withMember,
  type JsonObject
} from './payload-fields';

/**
 * Form editors for a usage's three jsonb payloads — `params`, `prefill` and `compute`.
 *
 * ## The shape is read off the server's schema, not off a list kept here
 *
 * `GET /vocabulary` carries `fieldTypes[].paramsSchema`: the JSON Schema the publish validator
 * actually gates on. There are twenty-two field types but only **seven distinct params
 * shapes**, because the server splices the same schema constants into whole families of types
 * — every scalar shares one, every choice type another. {@link paramsShape} classifies a type
 * by looking at which members its schema declares, so:
 *
 * - a new field type that reuses an existing shape gets the right form with no release here;
 * - a genuinely new shape falls through to `unknown` and gets the JSON box, rather than being
 *   rendered by whichever form happened to be closest.
 *
 * That is the answer to the objection the authoring-UI design raised against forms in the
 * first place — that a hand-built form would be a second source of truth for a grammar the
 * server owns. It is one source of truth, read at runtime.
 *
 * ## Nothing is dropped
 *
 * Each editor declares the members it renders; anything else on the value is listed by
 * {@link UnknownKeysNotice} and saved untouched. These are live definitions written before
 * this screen existed, and a form that silently rewrote them to only what it understands
 * would be worse than the text area it replaced.
 */

// ---------------------------------------------------------------------------
// Which form to show
// ---------------------------------------------------------------------------

export type ParamsShape =
  | 'none'
  | 'scalar'
  | 'choice'
  | 'grid'
  | 'repeat'
  | 'multi-year'
  | 'percent-group'
  | 'unknown';

/** The schema's top-level `properties`, or an empty object for a schema that declares none. */
function schemaProperties(schema: unknown): JsonObject {
  return asObject(asObject(schema)?.properties) ?? {};
}

/**
 * Classify a field type by what its params schema declares.
 *
 * Order matters only in that the composite markers are checked before the flat ones; no
 * schema carries two of them, and a schema that did would be a server-side change this screen
 * should not try to guess at.
 */
export function paramsShape(schema: unknown): ParamsShape {
  const properties = schemaProperties(schema);
  const keys = Object.keys(properties);

  if (keys.length === 0) return 'none';
  if ('grid' in properties) return 'grid';
  if ('repeat' in properties) return 'repeat';
  if ('multi_year' in properties) return 'multi-year';
  if ('percent_group' in properties) return 'percent-group';
  if ('constraint' in properties) return 'scalar';
  if ('variants' in properties) return 'choice';
  return 'unknown';
}

/** Members each shape renders. Anything outside its set is reported rather than dropped. */
const KNOWN_KEYS: Record<ParamsShape, string[]> = {
  none: [],
  scalar: ['constraint'],
  choice: ['variants', 'active_variant'],
  grid: ['grid'],
  repeat: ['repeat'],
  'multi-year': ['multi_year'],
  'percent-group': ['percent_group'],
  unknown: []
};

function unknownKeys(value: unknown, known: string[]): string[] {
  const object = asObject(value);
  if (!object) return [];
  return Object.keys(object).filter((key) => !known.includes(key));
}

/**
 * Enum members of a schema node, wherever the server spelled them — `enum` for a set,
 * `const` for a vocabulary of exactly one.
 */
function schemaEnum(node: unknown): string[] {
  const object = asObject(node);
  if (!object) return [];
  if (Array.isArray(object.enum)) return object.enum.filter((v): v is string => typeof v === 'string');
  if (typeof object.const === 'string') return [object.const];
  return [];
}

function optionsOf(values: readonly string[]): { value: string }[] {
  return values.map((value) => ({ value }));
}

// ---------------------------------------------------------------------------
// params
// ---------------------------------------------------------------------------

export interface IPayloadContext {
  vocabulary: Nullable<IVocabulary>;
  /** Usage codes in this version, for the members that name another question. */
  usageCodes: string[];
  disabled: boolean;
}

/** `optionsOf`, exported so the predicate editor builds its selects the same way. */
export { optionsOf };

/**
 * An `AnswerRef` — `{usage, instance?, cell?}` — which points at one answer.
 *
 * Shared because three grammars use the same shape: a compute argument, a predicate's
 * subject, and a `sum` predicate's comparison target. Spelling it once keeps "row" and "cell"
 * meaning the same thing wherever an author meets them.
 */
export function AnswerRefField({
  value,
  onChange,
  context,
  label = 'Question'
}: {
  value: unknown;
  onChange: (next: JsonObject) => void;
  context: IPayloadContext;
  label?: string;
}) {
  const ref = asObject(value) ?? {};
  const write = (key: string, next: unknown) => onChange(withMember(ref, key, next));

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <ChoiceField
        label={label}
        value={ref.usage}
        options={optionsOf(context.usageCodes)}
        allowUnset={false}
        onChange={(next) => write('usage', next)}
        disabled={context.disabled}
      />
      <TextField
        label="Row"
        hint="Inside a repeat group."
        value={ref.instance}
        onChange={(next) => write('instance', next)}
        disabled={context.disabled}
        mono
      />
      <TextField
        label="Cell"
        hint="Inside a grid."
        value={ref.cell}
        onChange={(next) => write('cell', next)}
        disabled={context.disabled}
        mono
      />
    </div>
  );
}

export function ParamsEditor({
  schema,
  fieldTypeKnown,
  value,
  onChange,
  context
}: {
  /** The chosen field type's `paramsSchema`, or null when the type is not resolved. */
  schema: unknown;
  /** False when the field type is inherited and the bank row has not been read. */
  fieldTypeKnown: boolean;
  value: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
}) {
  const shape = fieldTypeKnown ? paramsShape(schema) : 'unknown';
  const [json, setJson] = React.useState(false);
  const extra = unknownKeys(value, KNOWN_KEYS[shape]);

  const description =
    shape === 'unknown'
      ? fieldTypeKnown
        ? 'This field type has a parameter shape this screen does not know. Edit it as JSON.'
        : 'Pick a field type, or name a bank question, and the parameters for it appear here.'
      : undefined;

  return (
    <div className="grid gap-3">
      <PayloadHeader
        label="Parameters"
        description={description}
        json={json || shape === 'unknown'}
        onJson={setJson}
        disabled={shape === 'unknown'}
      />

      {json || shape === 'unknown' ? (
        <RawJsonField label="params" value={value} onChange={onChange} disabled={context.disabled} />
      ) : (
        <>
          <UnknownKeysNotice keys={extra} onOpenJson={() => setJson(true)} />
          <ParamsForm
            shape={shape}
            schema={schema}
            value={value}
            onChange={onChange}
            context={context}
          />
        </>
      )}
    </div>
  );
}

function ParamsForm({
  shape,
  schema,
  value,
  onChange,
  context
}: {
  shape: ParamsShape;
  schema: unknown;
  value: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
}) {
  const object = asObject(value);

  /** Replaces one top-level member, preserving everything the form does not render. */
  const setMember = (key: string, next: unknown) =>
    onChange(emptyToNull(withMember(object, key, next)));

  switch (shape) {
    case 'none':
      return (
        <p className="text-sm text-muted-foreground">
          This field type takes no parameters.
        </p>
      );

    case 'scalar':
      return (
        <ConstraintFields
          schema={asObject(schemaProperties(schema).constraint)}
          value={object?.constraint}
          onChange={(next) => setMember('constraint', next)}
          context={context}
        />
      );

    case 'choice':
      return (
        <div className="grid gap-3">
          <TextField
            label="Active variant"
            hint="The variant key this usage shows. Blank uses the default set."
            value={object?.active_variant}
            onChange={(next) => setMember('active_variant', next)}
            disabled={context.disabled}
            mono
          />
          <RawJsonField
            label="Variants"
            hint="Open-ended on the server — its schema is a bare object — so this one stays JSON."
            value={object?.variants ?? null}
            onChange={(next) => setMember('variants', next)}
            disabled={context.disabled}
            rows={4}
          />
        </div>
      );

    case 'grid':
      return (
        <GridFields
          schema={schemaProperties(schema).grid}
          value={object?.grid}
          onChange={(next) => setMember('grid', next)}
          context={context}
        />
      );

    case 'multi-year':
      return (
        <MultiYearFields
          schema={schemaProperties(schema).multi_year}
          value={object?.multi_year}
          onChange={(next) => setMember('multi_year', next)}
          context={context}
        />
      );

    case 'repeat':
      return (
        <RepeatFields
          schema={schemaProperties(schema).repeat}
          value={object?.repeat}
          onChange={(next) => setMember('repeat', next)}
          context={context}
        />
      );

    case 'percent-group':
      return (
        <PercentGroupFields
          schema={schemaProperties(schema).percent_group}
          value={object?.percent_group}
          onChange={(next) => setMember('percent_group', next)}
          context={context}
        />
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// The constraint block, shared by scalars and numeric grid columns
// ---------------------------------------------------------------------------

function ConstraintFields({
  schema,
  value,
  onChange,
  context
}: {
  schema: unknown;
  value: unknown;
  onChange: (next: Nullable<JsonObject>) => void;
  context: IPayloadContext;
}) {
  const object = asObject(value);
  const properties = schemaProperties(schema);
  const set = (key: string, next: unknown) => onChange(emptyToNull(withMember(object, key, next)));

  const formats = context.vocabulary?.formats ?? [];
  // `max_from` is a one-token vocabulary the server spells with `const`; read it from the
  // schema so a second token added there needs no release here.
  const maxFrom = schemaEnum(properties.max_from);

  return (
    <div className="grid gap-3 rounded-md border border-border p-3">
      <div className="flex items-center gap-2">
        <h5 className="text-sm font-medium">Constraint</h5>
        {object !== null && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            disabled={context.disabled}
            onClick={() => onChange(null)}
          >
            Clear
          </Button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <ChoiceField
          label="Format"
          hint="Validated on the server; the mask and message come with it."
          value={object?.format}
          options={formats.map((format) => ({
            value: format.code,
            note: format.mask ?? undefined
          }))}
          onChange={(next) => set('format', next)}
          disabled={context.disabled}
        />
        <NumberField
          label="Maximum length"
          value={object?.max_length}
          onChange={(next) => set('max_length', next)}
          disabled={context.disabled}
          integer
          min={1}
        />
        <NumberField
          label="Minimum"
          value={object?.min}
          onChange={(next) => set('min', next)}
          disabled={context.disabled}
        />
        <NumberField
          label="Maximum"
          value={object?.max}
          onChange={(next) => set('max', next)}
          disabled={context.disabled}
        />
        {maxFrom.length > 0 && (
          <ChoiceField
            label="Maximum from"
            hint="A moving ceiling the server resolves per cycle, instead of a fixed maximum."
            value={object?.max_from}
            options={optionsOf(maxFrom)}
            onChange={(next) => set('max_from', next)}
            disabled={context.disabled}
          />
        )}
        <NumberField
          label="Decimals"
          value={object?.decimals}
          onChange={(next) => set('decimals', next)}
          disabled={context.disabled}
          integer
          min={0}
          max={28}
        />
        <TriStateField
          label="Signed"
          hint="Whether a negative value is accepted."
          value={object?.signed}
          onChange={(next) => set('signed', next)}
          disabled={context.disabled}
          allowNull={false}
        />
        <ChoiceField
          label="Severity"
          value={object?.severity}
          options={optionsOf(schemaEnum(properties.severity))}
          onChange={(next) => set('severity', next)}
          disabled={context.disabled}
        />
        <ChoiceField
          label="Blocks at"
          hint="Where a breach stops the respondent."
          value={object?.block}
          options={optionsOf(schemaEnum(properties.block))}
          onChange={(next) => set('block', next)}
          disabled={context.disabled}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid and multi-year columns
// ---------------------------------------------------------------------------

/** The column schema, reachable the same way under both parents. */
function columnItemSchema(parentSchema: unknown): unknown {
  return asObject(schemaProperties(parentSchema).columns)?.items;
}

/**
 * Which column inputs may carry a constraint.
 *
 * Read off the schema's own `if`/`then` pair — it names the inputs that may **not** — rather
 * than restated here, because the server calls that rule out explicitly (design §4.4: a
 * checkbox cell is a bare boolean, radio and dropdown are already bounded by their options,
 * and readonly_text holds no respondent value). The fallback covers a schema shaped
 * differently from the one this was written against.
 */
function constraintAllowedInputs(itemSchema: unknown, all: string[]): string[] {
  const guarded = schemaEnum(asObject(schemaProperties(asObject(itemSchema)?.if).input));
  return guarded.length > 0 ? all.filter((input) => !guarded.includes(input)) : all;
}

function ColumnsField({
  parentSchema,
  value,
  onChange,
  context
}: {
  parentSchema: unknown;
  value: unknown;
  onChange: (next: unknown[]) => void;
  context: IPayloadContext;
}) {
  const columns = asArray(value);
  const itemSchema = columnItemSchema(parentSchema);
  const inputs = (context.vocabulary?.gridColumnInputs ?? []).map((entry) => entry.input);
  const allowsConstraint = constraintAllowedInputs(itemSchema, inputs);

  const replace = (index: number, next: JsonObject) =>
    onChange(columns.map((entry, at) => (at === index ? next : entry)));

  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Columns</span>
        <Badge variant="outline">{columns.length}</Badge>
        {!context.disabled && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => onChange([...columns, { code: '', input: inputs[0] ?? 'text' }])}
          >
            <Plus className="size-4" aria-hidden /> Add column
          </Button>
        )}
      </div>

      {columns.length === 0 && (
        <p className="text-xs text-muted-foreground">
          At least one column is required before this can publish.
        </p>
      )}

      {columns.map((entry, index) => {
        const column = asObject(entry) ?? {};
        const input = asString(column.input);
        const set = (key: string, next: unknown) => replace(index, withMember(column, key, next));

        return (
          <RepeaterItem
            key={index}
            title={`Column ${index + 1}${column.code ? ` · ${asString(column.code)}` : ''}`}
            disabled={context.disabled}
            onRemove={() => onChange(columns.filter((_, at) => at !== index))}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Code"
                value={column.code}
                onChange={(next) => set('code', next)}
                disabled={context.disabled}
                mono
              />
              <ChoiceField
                label="Input"
                value={input}
                options={optionsOf(inputs)}
                allowUnset={false}
                onChange={(next) => {
                  // Switching to an input the schema forbids a constraint on has to take the
                  // constraint with it, or the row becomes unpublishable with nothing on
                  // screen to explain why.
                  const moved = withMember(column, 'input', next);
                  replace(index, next && allowsConstraint.includes(next) ? moved : withMember(moved, 'constraint', null));
                }}
                disabled={context.disabled}
              />
            </div>

            <StringListField
              label="Options"
              hint="For radio and dropdown columns."
              values={asArray(column.options).map((option) => asString(option))}
              onChange={(next) => set('options', next.length === 0 ? null : next)}
              disabled={context.disabled}
              addLabel="Add option"
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Shown when cell"
                hint="Another column's code, or blank for always."
                value={asObject(column.when)?.cell}
                onChange={(next) =>
                  set('when', next === null ? null : withMember(asObject(column.when), 'cell', next))
                }
                disabled={context.disabled}
                mono
              />
              {asObject(column.when)?.cell !== undefined && (
                <RawJsonField
                  label="…equals"
                  hint="Any JSON value — the server does not narrow this one."
                  value={asObject(column.when)?.eq ?? null}
                  onChange={(next) => set('when', withMember(asObject(column.when), 'eq', next))}
                  disabled={context.disabled}
                  rows={2}
                />
              )}
            </div>

            {allowsConstraint.includes(input) ? (
              <ConstraintFields
                schema={schemaProperties(itemSchema).constraint}
                value={column.constraint}
                onChange={(next) => set('constraint', next)}
                context={context}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                A <code>{input || 'non-numeric'}</code> column takes no constraint.
              </p>
            )}
          </RepeaterItem>
        );
      })}
    </div>
  );
}

function GridFields({
  schema,
  value,
  onChange,
  context
}: {
  schema: unknown;
  value: unknown;
  onChange: (next: Nullable<JsonObject>) => void;
  context: IPayloadContext;
}) {
  const object = asObject(value);
  const set = (key: string, next: unknown) => onChange(emptyToNull(withMember(object, key, next)));

  return (
    <div className="grid gap-3">
      <TextField
        label="Row option list"
        hint="The list whose options become the rows. A list code, never a version."
        value={object?.row_option_list}
        onChange={(next) => set('row_option_list', next)}
        disabled={context.disabled}
        mono
      />
      <ColumnsField
        parentSchema={schema}
        value={object?.columns}
        onChange={(next) => set('columns', next)}
        context={context}
      />
    </div>
  );
}

function MultiYearFields({
  schema,
  value,
  onChange,
  context
}: {
  schema: unknown;
  value: unknown;
  onChange: (next: Nullable<JsonObject>) => void;
  context: IPayloadContext;
}) {
  const object = asObject(value);
  const set = (key: string, next: unknown) => onChange(emptyToNull(withMember(object, key, next)));
  const offsets = asArray(object?.offsets);
  // `labels_from` is a const in the schema: there is one legal value and no choice to offer,
  // so it is written rather than asked for.
  const labelsFrom = schemaEnum(schemaProperties(schema).labels_from)[0] ?? 'cycle.year_labels';

  return (
    <div className="grid gap-3">
      <div className="grid gap-1">
        <span className="text-sm font-medium">Year offsets</span>
        <span className="text-xs text-muted-foreground">
          Relative to the cycle year, and never positive — 0 is the current year, −1 the one
          before it.
        </span>
        <div className="flex flex-wrap items-center gap-1">
          {offsets.map((offset, index) => (
            <span key={index} className="flex items-center gap-1">
              <Input
                type="number"
                max={0}
                step={1}
                className="w-20"
                aria-label={`Offset ${index + 1}`}
                value={typeof offset === 'number' ? String(offset) : ''}
                disabled={context.disabled}
                onChange={(event) => {
                  const parsed = Number.parseInt(event.target.value, 10);
                  set(
                    'offsets',
                    offsets.map((entry, at) =>
                      at === index ? (Number.isFinite(parsed) ? parsed : 0) : entry
                    )
                  );
                }}
              />
              {!context.disabled && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove offset ${index + 1}`}
                  onClick={() => set('offsets', offsets.filter((_, at) => at !== index))}
                >
                  ×
                </Button>
              )}
            </span>
          ))}
          {!context.disabled && (
            <Button variant="outline" size="sm" onClick={() => set('offsets', [...offsets, 0])}>
              <Plus className="size-4" aria-hidden /> Add year
            </Button>
          )}
        </div>
      </div>

      <FieldRow label="Column labels from" hint="Fixed by the server — there is one legal value.">
        <Input value={labelsFrom} readOnly className="font-mono text-xs" />
      </FieldRow>

      <ColumnsField
        parentSchema={schema}
        value={object?.columns}
        onChange={(next) => set('columns', next)}
        context={context}
      />
    </div>
  );
}

function RepeatFields({
  schema,
  value,
  onChange,
  context
}: {
  schema: unknown;
  value: unknown;
  onChange: (next: Nullable<JsonObject>) => void;
  context: IPayloadContext;
}) {
  const object = asObject(value);
  const properties = schemaProperties(schema);
  const set = (key: string, next: unknown) => onChange(emptyToNull(withMember(object, key, next)));
  const mode = asString(object?.mode);

  return (
    <div className="grid gap-3">
      <ChoiceField
        label="Mode"
        hint="How many rows exist, and who decides."
        value={mode}
        options={optionsOf(schemaEnum(properties.mode))}
        allowUnset={false}
        onChange={(next) => set('mode', next)}
        disabled={context.disabled}
      />

      {/* Only the member its mode uses is offered: a `count_from_usage` on an unbounded
          repeat is not wrong so much as inert, and offering it invites filling it in. */}
      {mode === 'count_driven' && (
        <ChoiceField
          label="Row count from"
          hint="The question in this version whose answer decides how many rows appear."
          value={object?.count_from_usage}
          options={optionsOf(context.usageCodes)}
          onChange={(next) => set('count_from_usage', next)}
          disabled={context.disabled}
        />
      )}

      {mode === 'data_driven' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <ChoiceField
            label="Fact key"
            hint="The external fact whose rows drive this group."
            value={object?.fact_key}
            options={optionsOf(context.vocabulary?.factKeys ?? [])}
            onChange={(next) => set('fact_key', next)}
            disabled={context.disabled}
          />
          <TextField
            label="Instance key path"
            hint="What identifies one row inside that fact."
            value={object?.instance_key_path}
            onChange={(next) => set('instance_key_path', next)}
            disabled={context.disabled}
            mono
          />
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <TriStateField
          label="Respondent can add rows"
          value={object?.user_can_add}
          onChange={(next) => set('user_can_add', next)}
          disabled={context.disabled}
        />
        <TriStateField
          label="Respondent can remove rows"
          value={object?.user_can_remove}
          onChange={(next) => set('user_can_remove', next)}
          disabled={context.disabled}
        />
        <NumberField
          label="Minimum rows"
          value={object?.min}
          onChange={(next) => set('min', next)}
          disabled={context.disabled}
          integer
        />
        <NumberField
          label="Maximum rows"
          value={object?.max}
          onChange={(next) => set('max', next)}
          disabled={context.disabled}
          integer
        />
      </div>
    </div>
  );
}

function PercentGroupFields({
  schema,
  value,
  onChange,
  context
}: {
  schema: unknown;
  value: unknown;
  onChange: (next: Nullable<JsonObject>) => void;
  context: IPayloadContext;
}) {
  const object = asObject(value);
  const properties = schemaProperties(schema);
  const set = (key: string, next: unknown) => onChange(emptyToNull(withMember(object, key, next)));

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <ChoiceField
        label="Sum rule"
        hint="Whether the members have to add up to 100."
        value={object?.sum_rule}
        options={optionsOf(schemaEnum(properties.sum_rule))}
        allowUnset={false}
        onChange={(next) => set('sum_rule', next)}
        disabled={context.disabled}
      />
      <ChoiceField
        label="Severity"
        value={object?.severity}
        options={optionsOf(schemaEnum(properties.severity))}
        onChange={(next) => set('severity', next)}
        disabled={context.disabled}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// prefill
// ---------------------------------------------------------------------------

const PREFILL_KEYS = ['chain', 'editability', 'flag_on_edit'];

/**
 * `{ chain: [{source, field}], editability, flag_on_edit? }`.
 *
 * The chain is ordered and the order is the meaning — the first step that produces a value
 * wins — so it is a list with explicit positions rather than a set of checkboxes.
 */
export function PrefillEditor({
  value,
  onChange,
  context
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
}) {
  const [json, setJson] = React.useState(false);
  const object = asObject(value);
  const chain = asArray(object?.chain);
  const extra = unknownKeys(value, PREFILL_KEYS);

  const set = (key: string, next: unknown) =>
    onChange(emptyToNull(withMember(object, key, next)));

  const sources = context.vocabulary?.prefillSources ?? [];
  const editability = context.vocabulary?.prefillEditability ?? [];

  return (
    <div className="grid gap-3">
      <PayloadHeader
        label="Prefill"
        description="Where an answer arrives from before the respondent sees it, and what they may do to it."
        json={json}
        onJson={setJson}
      />

      {json ? (
        <RawJsonField label="prefill" value={value} onChange={onChange} disabled={context.disabled} />
      ) : object === null ? (
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground">Nothing is prefilled.</p>
          {!context.disabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onChange({ chain: [{ source: sources[0] ?? '', field: '' }], editability: editability[0] ?? 'free' })
              }
            >
              <Plus className="size-4" aria-hidden /> Add prefill
            </Button>
          )}
        </div>
      ) : (
        <>
          <UnknownKeysNotice keys={extra} onOpenJson={() => setJson(true)} />

          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Chain</span>
              <span className="text-xs text-muted-foreground">
                Tried in order; the first step with a value wins.
              </span>
              {!context.disabled && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() => set('chain', [...chain, { source: sources[0] ?? '', field: '' }])}
                >
                  <Plus className="size-4" aria-hidden /> Add step
                </Button>
              )}
            </div>

            {chain.length === 0 && (
              <p className="text-xs text-destructive">
                A prefill with an empty chain is refused at publish.
              </p>
            )}

            {chain.map((entry, index) => {
              const step = asObject(entry) ?? {};
              return (
                <RepeaterItem
                  key={index}
                  title={`Step ${index + 1}`}
                  disabled={context.disabled}
                  onRemove={() => set('chain', chain.filter((_, at) => at !== index))}
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ChoiceField
                      label="Source"
                      value={step.source}
                      options={optionsOf(sources)}
                      allowUnset={false}
                      onChange={(next) =>
                        set('chain', chain.map((e, at) => (at === index ? withMember(step, 'source', next) : e)))
                      }
                      disabled={context.disabled}
                    />
                    <TextField
                      label="Field"
                      hint="The member of that source to read."
                      value={step.field}
                      onChange={(next) =>
                        set('chain', chain.map((e, at) => (at === index ? withMember(step, 'field', next) : e)))
                      }
                      disabled={context.disabled}
                      mono
                    />
                  </div>
                </RepeaterItem>
              );
            })}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceField
              label="Editability"
              hint="Locked shows it read-only; confirm asks the respondent to agree; free lets them type over it."
              value={object.editability}
              options={optionsOf(editability)}
              allowUnset={false}
              onChange={(next) => set('editability', next)}
              disabled={context.disabled}
            />
            <TriStateField
              label="Flag on edit"
              hint="Raise a review flag when the respondent changes a prefilled value."
              value={object.flag_on_edit}
              onChange={(next) => set('flag_on_edit', next)}
              disabled={context.disabled}
              allowNull={false}
            />
          </div>

          {!context.disabled && (
            <Button variant="ghost" size="sm" className="w-fit" onClick={() => onChange(null)}>
              Remove prefill
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// compute
// ---------------------------------------------------------------------------

const COMPUTE_KEYS = ['fn', 'of', 'refs'];

/**
 * `{ fn, of? | refs? }`, where a ref is `{usage, instance?, cell?}`.
 *
 * The server accepts the arguments in two spellings — flat beside `fn`, or nested under an
 * `args` wrapper. This form writes the flat one, and a value using the wrapper shows up in
 * the unknown-members notice with the JSON view offered rather than being quietly rewritten
 * into the other spelling.
 */
export function ComputeEditor({
  value,
  onChange,
  context
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
}) {
  const [json, setJson] = React.useState(false);
  const object = asObject(value);
  const extra = unknownKeys(value, COMPUTE_KEYS);

  const functions = context.vocabulary?.computeFunctions ?? [];
  const set = (key: string, next: unknown) => onChange(emptyToNull(withMember(object, key, next)));

  const single = object !== null && 'of' in object;
  const refs = single ? [object.of] : asArray(object?.refs);

  const writeRefs = (next: unknown[]) => {
    const base = withMember(withMember(object, 'of', null), 'refs', null);
    onChange(emptyToNull(single ? withMember(base, 'of', next[0] ?? null) : withMember(base, 'refs', next)));
  };

  return (
    <div className="grid gap-3">
      <PayloadHeader
        label="Computed value"
        description="A value the server derives from other answers instead of asking for it."
        json={json}
        onJson={setJson}
      />

      {json ? (
        <RawJsonField label="compute" value={value} onChange={onChange} disabled={context.disabled} />
      ) : object === null ? (
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground">Not computed — the respondent answers it.</p>
          {!context.disabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange({ fn: functions[0] ?? '', refs: [] })}
            >
              <Plus className="size-4" aria-hidden /> Make it computed
            </Button>
          )}
        </div>
      ) : (
        <>
          <UnknownKeysNotice keys={extra} onOpenJson={() => setJson(true)} />

          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceField
              label="Function"
              value={object.fn}
              options={optionsOf(functions)}
              allowUnset={false}
              onChange={(next) => set('fn', next)}
              disabled={context.disabled}
            />
            <ChoiceField
              label="Takes"
              hint="One answer, or a list of them."
              value={single ? 'of' : 'refs'}
              options={[
                { value: 'of', label: 'A single answer' },
                { value: 'refs', label: 'Several answers' }
              ]}
              allowUnset={false}
              onChange={(next) => {
                const kept = refs.filter(Boolean);
                const base = withMember(withMember(object, 'of', null), 'refs', null);
                onChange(
                  next === 'of'
                    ? withMember(base, 'of', kept[0] ?? { usage: '' })
                    : withMember(base, 'refs', kept)
                );
              }}
              disabled={context.disabled}
            />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{single ? 'Answer' : 'Answers'}</span>
              {!single && !context.disabled && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() => writeRefs([...refs, { usage: '' }])}
                >
                  <Plus className="size-4" aria-hidden /> Add answer
                </Button>
              )}
            </div>

            {refs.map((entry, index) => {
              const ref = asObject(entry) ?? {};

              return (
                <RepeaterItem
                  key={index}
                  title={single ? 'Answer' : `Answer ${index + 1}`}
                  disabled={context.disabled || single}
                  onRemove={() => writeRefs(refs.filter((_, at) => at !== index))}
                >
                  <AnswerRefField
                    value={ref}
                    onChange={(next) => writeRefs(refs.map((e, at) => (at === index ? next : e)))}
                    context={context}
                  />
                </RepeaterItem>
              );
            })}
          </div>

          {!context.disabled && (
            <Button variant="ghost" size="sm" className="w-fit" onClick={() => onChange(null)}>
              Stop computing this
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * What the usage form shows while the field type is inherited and unresolved.
 *
 * Not an error: inheriting is the normal case, and the bank row is one request away. It is
 * here so the parameters panel says why it is showing a JSON box rather than appearing broken.
 */
export function UnresolvedTypeNotice({ question }: { question: string }) {
  return (
    <Alert>
      <AlertTitle>The field type is inherited</AlertTitle>
      <AlertDescription>
        {question.trim() === ''
          ? 'Name a bank question above and its field type decides which parameters apply.'
          : `Could not read the field type of ${question}, so the parameter form cannot be chosen. Set a field type on this usage, or edit the parameters as JSON.`}
      </AlertDescription>
    </Alert>
  );
}
